import { fetch_entity_type_verification_bindings } from '@/services/onboarding_verification_service';
import { resolve_onboarding_journey_filter } from '@/services/onboarding_journey_filter_service';
import {
  extract_saved_answers,
  normalize_main_section,
  normalize_text
} from '@/services/onboarding_form_helpers';
import {
  materialize_repeatable_steps,
  prioritize_file_sections
} from './queue_form_helpers';
import { get_verification_binding_fallback } from '@/views/onboarding_system_views/Gates/Verification/manage_verification_helpers';

export const APPLICATION_REFERENCE_TAB_ID = 'application-reference';

export function applyLoadedFormState(instance, form_tree_response, saved_application_form) {
  const items = Array.isArray(form_tree_response?.data) ? form_tree_response.data : [];
  instance.allowed_actions = Array.isArray(form_tree_response?.allowed_actions) ? form_tree_response.allowed_actions : [];
  instance.selected_application_form_detail = saved_application_form || null;
  const extracted_saved_answers = extract_saved_answers(saved_application_form);
  const normalized_steps = items.map((item, index) => normalize_main_section(item, index));
  const runtime_steps = materialize_repeatable_steps({
    steps: normalized_steps,
    extracted_saved_answers,
    persisted_file_urls: instance.persisted_file_urls
  });
  instance.intake_steps = instance.is_documents_gate_flow
    ? prioritize_file_sections(runtime_steps, instance.uses_file, normalize_text)
    : runtime_steps;
  instance.prefill_default_answers();
}

export function getSelectedForm(application_forms, selected_application_form_id) {
  return application_forms.find((form) => String(form.id) === String(selected_application_form_id)) || null;
}

function get_form_entity_type_id(form = {}) {
  return String(form?.entity_type_id || form?.entity_type?.id || '').trim();
}

export function dispatch_queue_form_case_scope(instance, selected_form = {}) {
  const entity_type_id = String(instance.$route.query.entity_type_id || '').trim()
    || get_form_entity_type_id(selected_form);
  if (!entity_type_id) return;

  const journey_filter = resolve_onboarding_journey_filter(instance.$route);
  window.dispatchEvent(new CustomEvent('onboarding-case-scope-selected', {
    detail: {
      application_id: String(instance.$route.params.applicationId || ''),
      entity_type_id,
      journey: journey_filter.label || ''
    }
  }));
}

export function clear_system_loader_after_render(instance) {
  instance.$nextTick(() => {
    window.dispatchEvent(new CustomEvent('system-loader:hide'));
  });
}

export function reset_file_input_value(question_id) {
  if (typeof document === 'undefined') return;
  const input = document.getElementById(`question_${question_id}`);
  if (input) input.value = '';
}

export function show_queue_form_loader() {
  window.dispatchEvent(new CustomEvent('system-loader:show', {
    detail: 'Loading onboarding form...'
  }));
}

export function create_repeat_group_key() {
  return `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function empty_array() {
  return [];
}

export function empty_object() {
  return {};
}

export function clamp_step_number(step_number = 1, total_steps = 1) {
  const normalized_total = Math.max(1, Number(total_steps || 1));
  const normalized_step = Number(step_number || 1);
  if (!Number.isFinite(normalized_step)) return 1;
  return Math.min(Math.max(1, Math.round(normalized_step)), normalized_total);
}

function normalize_section_id(value = null) {
  return String(value ?? '').trim();
}

export function find_step_number_by_section_id(intake_steps = [], section_id = null) {
  const normalized_section_id = normalize_section_id(section_id);
  if (!normalized_section_id) return null;

  const matched_step = (Array.isArray(intake_steps) ? intake_steps : []).find((step) => (
    normalize_section_id(step?.id) === normalized_section_id
  ));
  return Number(matched_step?.number || 0) || null;
}

export function history_document_status_label(document = {}) {
  const verification_state = normalize_text(document?.verification_state);
  if (document?.is_verified === true || verification_state === 'verified') return 'Approved';
  if (verification_state === 'rejected') return 'Rejected';
  if (String(document?.url || '').trim()) return 'Pending review';
  return 'Awaiting upload';
}

export function compact_dependency_text(value = '') {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

function has_manual_bindings(binding_data = {}) {
  return Array.isArray(binding_data?.manual_bindings) && binding_data.manual_bindings.length > 0;
}

export async function fetch_verification_binding_data(entity_type_id, binding_options) {
  const scoped_data = await fetch_entity_type_verification_bindings(entity_type_id, binding_options)
    .catch(get_verification_binding_fallback);
  const has_scoped_manual_bindings = has_manual_bindings(scoped_data);
  const has_scoped_options = Boolean(binding_options?.project_id || binding_options?.sub_entity_type_id);
  if (has_scoped_manual_bindings || !has_scoped_options) {
    return scoped_data;
  }

  const unscoped_data = await fetch_entity_type_verification_bindings(entity_type_id)
    .catch(get_verification_binding_fallback);
  return has_manual_bindings(unscoped_data) ? unscoped_data : scoped_data;
}
